import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { SnackbarIcon } from './snackbar.model';
import { MultipleSnackbarContainerComponent } from './multiple-snackbar-container/multiple-snackbar-container.component';

export interface NotificationConfig {
  message: string;
  iconConfig?: SnackbarIcon;
  displayConfig?: MatSnackBarConfig;
  action?: {
    actionText: string;
    actionCallback: () => void;
  };
  id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private appRef = inject(ApplicationRef);
  private envInjector = inject(EnvironmentInjector);

  private isContainerInitialized = false;

  snackBars$: WritableSignal<NotificationConfig[]> = signal<
    NotificationConfig[]
  >([]);

  private defaultConfig: MatSnackBarConfig = {
    duration: 5000,
    verticalPosition: 'bottom',
    horizontalPosition: 'start',
    panelClass: 'twc-notification',
  };

  constructor() {
    this.attachSnackbarContainer();
  }

  showSnackbar(notification: NotificationConfig): void {
    notification.id = notification.id ?? Date.now();

    this.snackBars$.update((snackbars) => [notification, ...snackbars]);

    if (
      notification.displayConfig &&
      notification.displayConfig.duration !== 0
    ) {
      setTimeout(() => {
        this.removeNotification(notification.id!);
      }, notification.displayConfig.duration);
    }
  }

  removeNotification(notificationId: number): void {
    this.snackBars$.update((snackbars) =>
      snackbars.filter((t) => t.id !== notificationId)
    );
  }

  showSystemNotification(message: string, iconConfig?: SnackbarIcon) {
    this._addNotification({
      message,
      ...(iconConfig && { iconConfig }),
      displayConfig: { panelClass: ['twc-notification', 'system-panel'] },
    });
  }

  showSystemErrorNotification(
    message: string,
    iconConfig: SnackbarIcon = {
      name: 'twc:info',
    }
  ) {
    this._addNotification({
      message,
      ...(iconConfig && { iconConfig }),
      displayConfig: { panelClass: ['twc-notification', 'system-error-panel'] },
    });
  }

  showNotification(
    message: string,
    iconConfig: SnackbarIcon = {
      name: 'twc:on-time',
      className: 'twc-fill-primary',
    }
  ) {
    this._addNotification({ message, iconConfig });
  }

  showNotificationWithAction(
    message: string,
    action: {
      actionText: string;
      actionCallback: () => void;
    },
    displayConfig?: MatSnackBarConfig,
    iconConfig: SnackbarIcon = {
      name: 'twc:on-time',
      className: 'twc-fill-primary',
    }
  ): void {
    this._addNotification({
      message,
      iconConfig,
      displayConfig,
      action,
    });
  }

  private _addNotification({
    message,
    iconConfig,
    displayConfig = this.defaultConfig,
    action,
  }: {
    message: string;
    iconConfig?: SnackbarIcon;
    displayConfig?: MatSnackBarConfig;
    action?: {
      actionText: string;
      actionCallback: () => void;
    };
  }) {
    this.showSnackbar({
      message,
      iconConfig,
      displayConfig: {
        ...(this.defaultConfig && { ...this.defaultConfig, ...displayConfig }),
      },
      ...(action && {
        action: {
          actionText: action.actionText,
          actionCallback: action.actionCallback,
        },
      }),
    });
  }

  private attachSnackbarContainer(): void {
    if (this.isContainerInitialized) {
      return;
    }

    const customInjector = Injector.create({
      providers: [
        {
          provide: SnackbarService,
          useValue: this,
        },
      ],
      parent: this.envInjector,
    });

    const componentRef = createComponent(MultipleSnackbarContainerComponent, {
      environmentInjector: this.envInjector,
      elementInjector: customInjector,
    });

    this.appRef.attachView(componentRef.hostView);

    document.body.appendChild(
      (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0]
    );

    this.isContainerInitialized = true;
  }
}
